import CryptoJS from 'crypto-js';
import type { AuthStorageAdapter, LoggedOnUser } from '@actschurch/ccms-sdk';

const SECRET = import.meta.env.VITE_CLIENT_SECRET || 'dev-secret-key-change-in-production';
const STORAGE_KEY = 'ccms_auth_user';

/**
 * Secure Storage Adapter for SDK AuthProvider
 *
 * Implements AuthStorageAdapter interface with AES encryption for user data.
 * Used by SDK's AuthManager to persist authentication state securely.
 */
export function createSecureStorage(): AuthStorageAdapter {
  return {
    /**
     * Retrieve user from encrypted localStorage
     * @returns LoggedOnUser or null if not found/invalid
     */
    async getUser(): Promise<LoggedOnUser | null> {
      try {
        const cipher = localStorage.getItem(STORAGE_KEY);
        if (!cipher) return null;

        // Try to decrypt the data
        const bytes = CryptoJS.AES.decrypt(cipher, SECRET);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);

        // Check if decryption produced valid output
        if (!decrypted || decrypted.length === 0) {
          console.warn('[SecureStorage] Decryption produced empty result, clearing corrupted data');
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }

        // Try to parse as JSON
        const user = JSON.parse(decrypted) as LoggedOnUser;

        // Validate that we have essential user properties (userId is the primary key)
        if (!user || !user.userId) {
          console.warn('[SecureStorage] Invalid user data structure, clearing');
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }

        return user;
      } catch (error) {
        // Handle malformed UTF-8, JSON parse errors, etc.
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.warn('[SecureStorage] Failed to get user:', errorMessage);

        // Clear corrupted/incompatible data
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (clearError) {
          console.error('[SecureStorage] Failed to clear corrupted data:', clearError);
        }

        return null;
      }
    },

    /**
     * Store user in encrypted localStorage
     * @param user - LoggedOnUser to persist
     */
    async setUser(user: LoggedOnUser): Promise<void> {
      try {
        const json = JSON.stringify(user);
        const cipher = CryptoJS.AES.encrypt(json, SECRET).toString();
        localStorage.setItem(STORAGE_KEY, cipher);
      } catch (error) {
        console.error('[SecureStorage] Failed to set user:', error);
        throw error;
      }
    },

    /**
     * Remove user from localStorage
     */
    async removeUser(): Promise<void> {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.error('[SecureStorage] Failed to remove user:', error);
      }
    },
  };
}

/**
 * Check if encrypted user data exists in storage
 * @returns true if encrypted user data exists
 */
export function hasStoredUser(): boolean {
  return localStorage.getItem(STORAGE_KEY) !== null;
}

/**
 * Clear all stored user data (for logout/reset)
 */
export function clearStoredUser(): void {
  localStorage.removeItem(STORAGE_KEY);
}
